import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Globe2,
  Loader2,
  RefreshCcw,
  Search,
  Server,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { toast } from "react-hot-toast";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const fmt = {
  dash: (v) => (v === null || v === undefined || v === "" ? "—" : v),
  bool: (v) => {
    if (v === true || v === "true" || v === "1") return "Yes";
    if (v === false || v === "false" || v === "0") return "No";
    return "—";
  },
  date: (v) => {
    if (!v) return "—";

    try {
      return new Date(v).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return v;
    }
  },
};

export default function Domains({
  subdomains = [],
  summary = null,
  loading = false,
  busyById = {},
  onFetch,
  onDelete,
  domain = "chaafo.com",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
const [deleteModal, setDeleteModal] = useState({
  open: false,
  row: null,
  confirmText: "",
});
  const rows = useMemo(() => {
    if (!Array.isArray(subdomains)) return [];

    return subdomains.map((item, index) => {
      const name = item?.name || item?.domain || item?.subdomain || "";
      const content = item?.content || item?.subdomaincontent || item?.ip || "";
      const type = item?.type || "A";

      return {
        ...item,
        _index: index + 1,
        id: item?.id || item?.record_id || item?.pk_id || "",
        name,
        type,
        content,
        proxied: item?.proxied,
        proxiable: item?.proxiable,
        ttl: item?.ttl,
        created_on: item?.created_on || item?.createdAt || item?.created_at,
        modified_on: item?.modified_on || item?.modifiedAt || item?.updated_at,
      };
    });
  }, [subdomains]);

  const filteredRows = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();

    if (!value) return rows;

    return rows.filter((item) => {
      return (
        String(item?.name || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.content || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.type || "")
          .toLowerCase()
          .includes(value) ||
        String(item?.id || "")
          .toLowerCase()
          .includes(value)
      );
    });
  }, [rows, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedRows = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    const end = start + pageSize;

    return filteredRows.slice(start, end);
  }, [filteredRows, pageSize, safeCurrentPage]);

  const startRecord =
    filteredRows.length === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;

  const endRecord = Math.min(safeCurrentPage * pageSize, filteredRows.length);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleCopy = async (text) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  };

const handleDelete = (row) => {
  if (!row?.id) {
    toast.error("Domain ID missing");
    return;
  }

  setDeleteModal({
    open: true,
    row,
    confirmText: "",
  });
};

const closeDeleteModal = () => {
  setDeleteModal({
    open: false,
    row: null,
    confirmText: "",
  });
};

const confirmFinalDelete = async () => {
  const row = deleteModal.row;

  if (!row?.id) {
    toast.error("Domain ID missing");
    return;
  }

  if (deleteModal.confirmText !== row.name) {
    toast.error("Domain name does not match");
    return;
  }

  const res = await onDelete?.(row);

  if (res?.success !== false) {
    closeDeleteModal();
  }
};

  return (
    <div className="rounded-2xl border bg-white p-3">
      <div className="rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-white p-4">
        {/* Header */}
        <div className="mb-4 flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
              <Globe2 size={14} />
              Domain Records
            </div>

            <h2 className="text-lg font-semibold text-gray-900">
              Chaafo Subdomains
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              View, search, refresh and delete created subdomains for{" "}
              <span className="font-semibold text-gray-700">{domain}</span>.
            </p>
          </div>

          <button
            type="button"
            onClick={onFetch}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw
              size={16}
              className={loading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <StatCard
            icon={<Globe2 size={18} />}
            label="Total Domains"
            value={summary?.total ?? rows.length}
            helper="Fetched DNS records"
          />

          <StatCard
            icon={<Server size={18} />}
            label="Base Domain"
            value={domain}
            helper="Fixed domain"
          />

          <StatCard
            icon={<ShieldCheck size={18} />}
            label="Proxied Records"
            value={
              rows.filter(
                (item) =>
                  item?.proxied === true ||
                  item?.proxied === "true" ||
                  item?.proxied === "1"
              ).length
            }
            helper="Cloudflare proxy enabled"
          />
        </div>

        {/* Search + page size */}
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by domain, IP, type or ID..."
              className="h-11 w-full rounded-xl border bg-white pl-10 pr-3 text-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div className="flex items-center justify-between gap-3 lg:justify-end">
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-800">
                {startRecord}-{endRecord}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {filteredRows.length}
              </span>
            </p>

            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="h-10 rounded-xl border bg-white px-3 text-sm outline-none focus:border-emerald-400"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border bg-white">
          <div className="max-w-full overflow-x-auto">
            <table className="min-w-[1000px] w-full text-left">
              <thead className="border-b bg-gray-50">
                <tr>
                  <Th>#</Th>
                  <Th>Domain</Th>
                  <Th>Type</Th>
                  <Th>Content IP</Th>
                  <Th>Proxy</Th>
                  <Th>TTL</Th>
                  <Th>Created</Th>
                  <Th>Modified</Th>
                  <Th align="right">Action</Th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <TableLoader />
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => {
                    const isDeleting = !!busyById?.[row.id];

                    return (
                      <tr
                        key={row.id || `${row.name}-${index}`}
                        className="border-b last:border-b-0 hover:bg-emerald-50/40"
                      >
                        <Td>
                          <span className="text-sm font-semibold text-gray-500">
                            {(safeCurrentPage - 1) * pageSize + index + 1}
                          </span>
                        </Td>

                        <Td>
                          <div className="flex items-center gap-3">
                            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                              <Globe2 size={17} />
                            </div>

                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="max-w-[280px] truncate font-mono text-sm font-semibold text-gray-900">
                                  {fmt.dash(row.name)}
                                </p>

                                {row.name && (
                                  <button
                                    type="button"
                                    onClick={() => handleCopy(row.name)}
                                    className="grid h-7 w-7 place-items-center rounded-lg border text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                    title="Copy domain"
                                  >
                                    <Copy size={13} />
                                  </button>
                                )}
                              </div>

                              <p className="mt-0.5 max-w-[320px] truncate text-xs text-gray-400">
                                ID: {fmt.dash(row.id)}
                              </p>
                            </div>
                          </div>
                        </Td>

                        <Td>
                          <span className="inline-flex rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                            {fmt.dash(row.type)}
                          </span>
                        </Td>

                        <Td>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-gray-700">
                              {fmt.dash(row.content)}
                            </span>

                            {row.content && (
                              <button
                                type="button"
                                onClick={() => handleCopy(row.content)}
                                className="grid h-7 w-7 place-items-center rounded-lg border text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                title="Copy IP"
                              >
                                <Copy size={13} />
                              </button>
                            )}
                          </div>
                        </Td>

                        <Td>
                          <ProxyBadge value={row.proxied} />
                        </Td>

                        <Td>
                          <span className="font-mono text-sm text-gray-600">
                            {fmt.dash(row.ttl)}
                          </span>
                        </Td>

                        <Td>
                          <span className="text-sm text-gray-600">
                            {fmt.date(row.created_on)}
                          </span>
                        </Td>

                        <Td>
                          <span className="text-sm text-gray-600">
                            {fmt.date(row.modified_on)}
                          </span>
                        </Td>

                        <Td align="right">
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            disabled={isDeleting}
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-xl border border-rose-100 bg-rose-50 px-3 text-sm font-semibold text-rose-600 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isDeleting ? (
                              <>
                                <Loader2 size={15} className="animate-spin" />
                                Deleting
                              </>
                            ) : (
                              <>
                                <Trash2 size={15} />
                                Delete
                              </>
                            )}
                          </button>
                        </Td>
                      </tr>
                    );
                  })
                ) : (
                  <EmptyState searchTerm={searchTerm} />
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-gray-500">
            Page{" "}
            <span className="font-semibold text-gray-800">
              {safeCurrentPage}
            </span>{" "}
            of <span className="font-semibold text-gray-800">{totalPages}</span>
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={safeCurrentPage <= 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              className="inline-flex h-9 items-center gap-1 rounded-xl border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} />
              Prev
            </button>

            <PageNumbers
              totalPages={totalPages}
              currentPage={safeCurrentPage}
              onPageChange={setCurrentPage}
            />

            <button
              type="button"
              disabled={safeCurrentPage >= totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              className="inline-flex h-9 items-center gap-1 rounded-xl border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        {/* Delete Confirmation Modal */}
        {deleteModal.open && (
  <DeleteConfirmModal
    row={deleteModal.row}
    confirmText={deleteModal.confirmText}
    setConfirmText={(value) =>
      setDeleteModal((prev) => ({
        ...prev,
        confirmText: value,
      }))
    }
    onClose={closeDeleteModal}
    onConfirm={confirmFinalDelete}
    loading={!!busyById?.[deleteModal.row?.id]}
  />
)}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, helper }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {label}
          </p>

          <p className="mt-0.5 truncate text-base font-semibold text-gray-900">
            {value}
          </p>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-400">{helper}</p>
    </div>
  );
}

function ProxyBadge({ value }) {
  const isEnabled = value === true || value === "true" || value === "1";

  if (isEnabled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
        <CheckCircle2 size={13} />
        Proxied
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-500">
      <AlertTriangle size={13} />
      DNS Only
    </span>
  );
}

function PageNumbers({ totalPages, currentPage, onPageChange }) {
  const pages = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, "...", totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  }, [totalPages, currentPage]);

  return (
    <div className="hidden items-center gap-1 md:flex">
      {pages.map((page, index) => {
        if (page === "...") {
          return (
            <span
              key={`dots-${index}`}
              className="grid h-9 w-9 place-items-center text-sm text-gray-400"
            >
              ...
            </span>
          );
        }

        const active = page === currentPage;

        return (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            className={`grid h-9 w-9 place-items-center rounded-xl border text-sm font-semibold transition ${
              active
                ? "border-emerald-500 bg-emerald-500 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {page}
          </button>
        );
      })}
    </div>
  );
}

function TableLoader() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <tr key={index} className="border-b last:border-b-0">
          <Td>
            <Skeleton className="h-4 w-8" />
          </Td>

          <Td>
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />

              <div>
                <Skeleton className="h-4 w-44" />
                <Skeleton className="mt-2 h-3 w-32" />
              </div>
            </div>
          </Td>

          <Td>
            <Skeleton className="h-6 w-14 rounded-full" />
          </Td>

          <Td>
            <Skeleton className="h-4 w-32" />
          </Td>

          <Td>
            <Skeleton className="h-6 w-20 rounded-full" />
          </Td>

          <Td>
            <Skeleton className="h-4 w-10" />
          </Td>

          <Td>
            <Skeleton className="h-4 w-28" />
          </Td>

          <Td>
            <Skeleton className="h-4 w-28" />
          </Td>

          <Td align="right">
            <Skeleton className="ml-auto h-9 w-24 rounded-xl" />
          </Td>
        </tr>
      ))}
    </>
  );
}

function EmptyState({ searchTerm }) {
  return (
    <tr>
      <td colSpan={9} className="px-4 py-14 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gray-50 text-gray-400">
          <Globe2 size={24} />
        </div>

        <h3 className="mt-4 text-base font-semibold text-gray-900">
          No domains found
        </h3>

        <p className="mt-1 text-sm text-gray-500">
          {searchTerm
            ? "No records match your current search."
            : "Created subdomains will appear here after fetching data."}
        </p>
      </td>
    </tr>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td
      className={`px-4 py-3 align-middle ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </td>
  );
}

function Skeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-md bg-gray-200/80 ${className}`}
    />
  );
}
function DeleteConfirmModal({
  row,
  confirmText,
  setConfirmText,
  onClose,
  onConfirm,
  loading,
}) {
  const domainName = row?.name || "";
  const isMatched = confirmText === domainName;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b bg-rose-50 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-rose-100 text-rose-600">
              <AlertTriangle size={20} />
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Delete domain permanently?
              </h3>

              <p className="mt-1 text-sm text-gray-600">
                This action will delete the DNS record for this subdomain.
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3">
            <p className="text-sm text-rose-700">
              This is a destructive action. To confirm, type the domain name
              exactly as shown below:
            </p>

            <p className="mt-2 break-all rounded-lg bg-white px-3 py-2 font-mono text-sm font-semibold text-gray-900">
              {domainName}
            </p>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold text-gray-500">
              Type domain name to confirm
            </label>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={loading}
              placeholder={domainName}
              autoFocus
              className={`h-11 w-full rounded-xl border bg-white px-3 font-mono text-sm outline-none transition disabled:cursor-not-allowed disabled:bg-gray-50 ${
                confirmText
                  ? isMatched
                    ? "border-emerald-400 ring-2 ring-emerald-100"
                    : "border-rose-400 ring-2 ring-rose-100"
                  : "focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
              }`}
            />

            {confirmText && !isMatched && (
              <p className="mt-1 text-xs font-medium text-rose-600">
                Domain name does not match exactly.
              </p>
            )}

            {isMatched && (
              <p className="mt-1 text-xs font-medium text-emerald-600">
                Domain matched. You can delete now.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-xl border bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={!isMatched || loading}
            className="inline-flex h-10 min-w-[140px] items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} />
                Delete domain
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}